import { useEffect, useState, useRef } from "react";
import {
  CommunicationPreference,
  States,
  UserAccountType,
} from "../../__generated__/types";
import "./styles.scss";
import { UserTypeFieldset } from "./user-type-fieldset";
import { EmailPasswordFieldset } from "./email-password-fieldset";
import { VerificationCodeFieldset } from "./email-verification-fieldset";
import { PersonalDetailsFieldset } from "./personal-details-fieldset";
import { BusinessInformationFieldset } from "./business-information-fieldset";
import { TerritorySelectionFieldset } from "./territory-selection-fieldset";
import { UpdatingUserState } from "./updating-user-state";
import { useUserContext } from "../../hooks/userContext";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { Pages } from "../../utils";
import { PaymentTokenManager } from "../../services/paymentApi";
import {
  clearRegistrationProgressStep,
  clearRegistrationResumeIntent,
  getRegistrationProgressStep,
  hasRegistrationResumeIntent,
  setRegistrationProgressStep,
} from "../../utils/registration-progress";
import { HomeownerPlanSelectionFieldset, PlanKey } from "./homeowner-plan-selection-fieldset";
import { HomeownerZipFieldset } from "./homeowner-zip-fieldset";
import { HomeownerAddressFieldset } from "./homeowner-address-fieldset";
import { CustomPaymentModal } from "./custom-payment-modal";
import { HomeownerRegistrationCompletion } from "./homeowner-registration-completion";
import { usePlanContext } from "../../contexts/PlanContext";
import { PaymentFailureModal } from "./payment-failure-modal";
import { OnboardingGuard, EmailVerificationGuard, ProfileCompletionGuard, DesignerEmailVerificationGuard, DesignerProfileCompletionGuard } from "../../components/onboarding-guard";
import { useOnboardingValidation } from "../../hooks/useOnboardingValidation";
import { useDesignerOnboardingValidation } from "../../hooks/useDesignerOnboardingValidation";
import { Box, Typography, Stepper, Step, StepLabel, Alert } from "@mui/material";
import { useTrackAttributionEventMutation } from "../../hooks/useTrackAttributionEventMutation";
import {
  buildEventIdempotencyKey,
  captureAttributionFromLocation,
  getLastTouchAttributionInput,
  parseAttributionFromUrl,
  readAttributionCookie,
} from "../../utils/attribution";

const getEmailVerifiedFlag = (value: unknown): boolean | null => {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return null;
};

// const titles = {
//   3: "Personal Details",
//   4: "Business Information",
//   5: "Account Preferences",
// };

export const Registration = () => {
  const { user } = useUserContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeAccessGranted = useRef(false);
  const hasTrackedRegisterVisit = useRef(false);
  const { trackAttributionEvent } = useTrackAttributionEventMutation();

  const [loading, setLoading] = useState(false);
  const [resumingRegistration, setResumingRegistration] = useState(false);
  const finalizationStarted = useRef(false);

  const [userType, setUserType] = useState<UserAccountType>(() => {
    const parsed = parseAttributionFromUrl(
      typeof window !== "undefined"
        ? window.location.href
        : `?${searchParams.toString()}`
    );
    const urlType = parsed.userType || searchParams.get("userType");
    return urlType === "Homeowner"
      ? UserAccountType.Homeowner
      : UserAccountType.Designer;
  });

  // Check if user is returning from successful payment
  const paymentVerified = searchParams.get("payment_verified") === "true";

  // Enhanced payment data pre-population system
  const prePopulateFromPaymentData = (paymentData: any) => {
    
    // Set the user type to homeowner (since only homeowners have paid plans)
    setUserType(UserAccountType.Homeowner);
    
    // Set the selected plan based on payment data
    if (paymentData.planType === 'Guild' || paymentData.planName === 'Guild' || paymentData.productTier === 'guild') {
      setSelectedPlan('Guild');
    } else if (paymentData.planType === 'GuildPlus' || paymentData.planName === 'Guild+' || paymentData.productTier === 'guildplus') {
      setSelectedPlan('GuildPlus');
    }
    
    // Mark payment as completed
    setPaymentCompleted(true);
    
    // Pre-fill email if available
    if (paymentData.email) {
      setEmail(paymentData.email);
    }
    
    // Pre-fill personal details if available
    if (paymentData.customerInfo) {
      setFirstName(paymentData.customerInfo.firstName || '');
      setLastName(paymentData.customerInfo.lastName || '');
      setPhoneNumber(paymentData.customerInfo.phoneNumber || '');
      
      // Update homeowner address object with all available data
      setHomeownerAddress(prev => {
        const newAddress = {
          ...prev,
          firstName: paymentData.customerInfo.firstName || prev.firstName,
          lastName: paymentData.customerInfo.lastName || prev.lastName,
          phoneNumber: paymentData.customerInfo.phoneNumber || prev.phoneNumber,
          address: paymentData.customerInfo.address || prev.address,
          city: paymentData.customerInfo.city || prev.city,
          state: paymentData.customerInfo.state || prev.state,
          zipCode: paymentData.customerInfo.zipCode || prev.zipCode
        };
        return newAddress;
      });
      if (paymentData.customerInfo.zipCode) {
        setHomeownerZip(paymentData.customerInfo.zipCode);
      }
    }
    
  };

  // Handle payment verification on component load
  useEffect(() => {
    if (paymentVerified) {
      // Get payment data from session storage
      const paymentData = PaymentTokenManager.getPaymentData();
      if (paymentData) {
        prePopulateFromPaymentData(paymentData);
        
        // Set the step to 2 (Email/Password creation) since payment is complete
        // Use setTimeout to ensure userType state update has taken effect
        setTimeout(() => {
          setHomeownerStep(2);
        }, 0);
        
        // Navigate to clean URL without payment_verified parameter
        navigate('/register?userType=Homeowner', { replace: true });
      }
    }
  }, [paymentVerified, navigate]);

  useEffect(() => {
    if (hasTrackedRegisterVisit.current) {
      return;
    }

    captureAttributionFromLocation({
      href: typeof window !== "undefined" ? window.location.href : "",
      referrerUrl: typeof document !== "undefined" ? document.referrer : "",
    });

    const attributionCookie = readAttributionCookie();
    const lastTouchAttribution = getLastTouchAttributionInput(attributionCookie);
    const idempotencyKey = buildEventIdempotencyKey("register_page_visited", {
      userId: user?.id,
      anonymousId: lastTouchAttribution.anonymousId,
      variant: lastTouchAttribution.variant,
      scope: typeof window !== "undefined" ? window.location.pathname : "register",
    });

    hasTrackedRegisterVisit.current = true;

    void trackAttributionEvent({
      variables: {
        input: {
          idempotencyKey,
          userId: user?.id || undefined,
          anonymousId: lastTouchAttribution.anonymousId,
          eventType: "REGISTER_PAGE_VISITED",
          accountType: user?.accountType ? String(user.accountType) : undefined,
          variant: lastTouchAttribution.variant,
          sourceLandingPage: lastTouchAttribution.sourceLandingPage,
          referrerUrl: lastTouchAttribution.referrerUrl,
          occurredAt: lastTouchAttribution.occurredAt,
        },
      },
    }).catch((eventError) => {
      console.error("Failed to track register_page_visited event", eventError);
    });
  }, [trackAttributionEvent, user?.accountType, user?.id]);

  // Check for stored payment data on component mount (for users returning to registration)
  useEffect(() => {
    const paymentData = PaymentTokenManager.getPaymentData();
    
    if (paymentData && !paymentVerified) {
      prePopulateFromPaymentData(paymentData);
    }
  }, []); // Run once on mount

  // Note: Payment data should only be cleared after successful account creation
  // or when user explicitly cancels the registration process

  // Resume incomplete registration for logged-in users
  useEffect(() => {
    // Don't redirect if we're currently in the loading state (creating profile)
    if (loading) {
      return;
    }
    
    if (user && !resumingRegistration) {
      // Check if user data is incomplete (missing critical fields)
      let effectiveUser = user;
      if (!user.id && !user.email && !user.accountType) {
        // User data is incomplete, try to get it from sessionStorage
        const sessionUser = sessionStorage.getItem('resumeRegistrationUser');
        if (sessionUser) {
          try {
            effectiveUser = JSON.parse(sessionUser);
            
            // Update localStorage with complete user data so guards can access it
            localStorage.setItem('user', JSON.stringify(effectiveUser));
            
            // Force a re-render by reloading the page
            window.location.reload();
            return;
          } catch (e) {
          }
        }
      }
      

      // If user is already active, route by account type.
      if (effectiveUser.onboardingStatus === 'active') {
        if (effectiveUser.accountType === UserAccountType.Homeowner) {
          navigate(Pages.YOUR_HUB);
        } else {
          navigate(Pages.DASHBOARD);
        }
        return;
      }

      // Resume incomplete registration
      setResumingRegistration(true);

      // Set user type from logged-in user
      if (effectiveUser.accountType) {
        setUserType(effectiveUser.accountType as UserAccountType);
      }

      // Set email and user account ID
      // Prioritize email from payment data if available (user's current session)
      // Only fall back to user context email if no payment data exists
      const paymentData = PaymentTokenManager.getPaymentData();
      if (paymentData && paymentData.email) {
        setEmail(paymentData.email);
      } else if (effectiveUser.email) {
        setEmail(effectiveUser.email);
      }
      // Try multiple possible ID fields
      const userId = effectiveUser.id || (effectiveUser as any)._id || (effectiveUser as any).userId;
      if (userId) {
        setUserAccountId(userId);
      } 

      // Set email as verified if already verified
      if (getEmailVerifiedFlag(effectiveUser.emailVerified) === true) {
        setEmailVerified(true);
      }

      // Pre-populate existing data
      if (effectiveUser.firstName) setFirstName(effectiveUser.firstName);
      if (effectiveUser.lastName) setLastName(effectiveUser.lastName);
      if ((effectiveUser as any).phoneNumber) setPhoneNumber((effectiveUser as any).phoneNumber);
      
      // Set selected plan from user subscription
      // Subscription might be wrapped in an object { value: "..." } from GraphQL
      const userSubscription = (effectiveUser as any).subscription;
      const subscriptionValue =
        userSubscription && typeof userSubscription === 'object'
          ? userSubscription.homeownerValue ||
            userSubscription.designerValue ||
            userSubscription.value ||
            userSubscription.tier ||
            null
          : userSubscription;
      
      // IMPORTANT: Check for payment data first - it takes priority over user subscription
      // This handles the case where user paid but subscription hasn't been saved to DB yet
      const storedPaymentData = PaymentTokenManager.getPaymentData();
      if (storedPaymentData) {
        // Extract plan from payment data
        if (storedPaymentData.planType === 'Guild' || storedPaymentData.planName === 'Guild' || storedPaymentData.productTier === 'guild') {
          setSelectedPlan('Guild');
        } else if (storedPaymentData.planType === 'GuildPlus' || storedPaymentData.planName === 'Guild+' || storedPaymentData.productTier === 'guildplus') {
          setSelectedPlan('GuildPlus');
        }
        setPaymentCompleted(true);
      } else if (subscriptionValue) {
        setSelectedPlan(subscriptionValue);
      } else {
      }

      // Determine which step to resume from based on onboarding status
      // But don't override if URL already has explicit step parameter
      const urlStep = searchParams.get("step");
      const hasExplicitStep = urlStep && !isNaN(parseInt(urlStep, 10));
      const storedStep = !hasExplicitStep
        ? getRegistrationProgressStep(effectiveUser)
        : null;
      
      if (hasExplicitStep) {
        // Still set payment as completed if user has paid
        const paymentData = PaymentTokenManager.getPaymentData();
        if (paymentData || (effectiveUser as any).paymentStatus === 'paid') {
          setPaymentCompleted(true);
        }
      } else if (
        storedStep !== null &&
        storedStep > 0 &&
        effectiveUser.accountType === UserAccountType.Homeowner
      ) {
        setHomeownerStep(storedStep);
      } else if (
        storedStep !== null &&
        storedStep > 0 &&
        effectiveUser.accountType === UserAccountType.Designer
      ) {
        setDesignerStep(storedStep);
      } else if (getEmailVerifiedFlag(effectiveUser.emailVerified) === false) {
        // Force email verification step regardless of onboardingStatus
        if (effectiveUser.accountType === UserAccountType.Homeowner) {
          setHomeownerStep(2);
        } else if (effectiveUser.accountType === UserAccountType.Designer) {
          setDesignerStep(2);
        }
      } else if (effectiveUser.accountType === UserAccountType.Homeowner) {
        if (effectiveUser.onboardingStatus === 'pending_email_verification') {
          // Start at email verification step
          setHomeownerStep(2);
        } else if (effectiveUser.onboardingStatus === 'pending_profile_completion') {
          // Check if payment was completed
          const paymentData = PaymentTokenManager.getPaymentData();
          
          if (paymentData || (effectiveUser as any).paymentStatus === 'paid') {
            setPaymentCompleted(true);
            // Resume at personal details step
            setHomeownerStep(5);
          } else {
            // Resume at payment step
            setHomeownerStep(4);
          }
        }
      } else if (effectiveUser.accountType === UserAccountType.Designer) {
        // Similar logic for designers
        if (effectiveUser.onboardingStatus === 'pending_email_verification') {
          setDesignerStep(2);
        } else if (effectiveUser.onboardingStatus === 'pending_profile_completion') {
          setDesignerStep(3);
        }
      }

    }
  }, [user, navigate, resumingRegistration, searchParams]);


  // Enhanced onboarding validation
  const {
    currentStep,
    canAccessStep,
    getRequiredStep,
    userStatus
  } = useOnboardingValidation();

  // Designer-specific validation
  const {
    canAccessPersonalDetails,
    canAccessBusinessDetails,
    canAccessAccountPreferences,
    isEmailVerified: isDesignerEmailVerified,
    isPersonalDetailsComplete,
    isBusinessDetailsComplete,
    isAccountPreferencesComplete
  } = useDesignerOnboardingValidation();

  const isUserTypeValid = () =>
    userType !== undefined;

  const [selectedPlan, setSelectedPlan] = useState<PlanKey | null>(null);
  const [selectedPlanData, setSelectedPlanData] = useState<any>(null);
  
  // Get plan context for dynamic plan data
  const planContext = usePlanContext();
  
  const [billingAmount, setBillingAmount] = useState<number>(0);
  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [showPaymentFailureModal, setShowPaymentFailureModal] = useState<boolean>(false);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string>('');
  const isPlanSelected = () => userType !== UserAccountType.Homeowner || !!selectedPlan;

  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [confirmPassword, setConfirmPassword] = useState<string>();
  const [userAccountId, setUserAccountId] = useState<null | string>(null);

  const isEmailPasswordValid = () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailIsValid = emailPattern.test(email || "");
    const passwordIsValid = password?.length && password.length >= 8;
    const confirmPasswordIsValid = confirmPassword?.length && confirmPassword.length >= 8;
    const passwordsMatch = password === confirmPassword;
    // For new registrations from payment, always require email/password validation
    return !!(emailIsValid && passwordIsValid && confirmPasswordIsValid && passwordsMatch);
  };

  const [verificationCode, setVerificationCode] = useState<string>();
  const [emailVerified, setEmailVerified] = useState<boolean>(false);
  const isVerificationCodeValid = () => {
    // If email is already verified (user has moved past verification step), return true
    if (emailVerified) {
      return true;
    }
    // For new registrations, check if verification code is entered
    return !!(verificationCode && verificationCode.length >= 5);
  };

  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const isPersonalDetailsValid = () => !!(firstName && lastName && phoneNumber);

  const [homeownerAddress, setHomeownerAddress] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    zipCode: "",
    city: "",
    state: "" as States,
    address: "",
  });
  const [homeownerZip, setHomeownerZip] = useState("");
  const [homeownerDmaCode, setHomeownerDmaCode] = useState<string | null>(null);
  const [isInServiceArea, setIsInServiceArea] = useState<boolean | null>(null);

  useEffect(() => {
    if (isInServiceArea === false && selectedPlan === "GuildPlus") {
      setSelectedPlan(null);
      setSelectedPlanData(null);
    }
  }, [isInServiceArea, selectedPlan]);

  const [addressFormSubmitted, setAddressFormSubmitted] = useState(false);

  const isHomeownerAddressValid = () =>
  !!(
    homeownerAddress.firstName &&
    homeownerAddress.lastName &&
    homeownerAddress.phoneNumber &&
    homeownerAddress.zipCode &&
    homeownerAddress.city &&
    homeownerAddress.state &&
    homeownerAddress.address
  );

  const [businessName, setBusinessName] = useState<string>();
  const [businessAddress, setBusinessAddress] = useState<string>();
  const [businessZipCode, setBusinessZipCode] = useState<string>();
  const [businessCity, setBusinessCity] = useState<string>();
  const [businessState, setBusinessState] = useState<States>();
  const [businessWebsite, setBusinessWebsite] = useState<string>();
  const isBusinessDetailsValid = () =>
    !!(businessName && businessAddress && businessZipCode && businessCity && businessState);

  const [workLocation, setWorkLocation] = useState<string[]>([]);
  const [communicationPreference, setCommunicationPreference] = useState<CommunicationPreference[]>([]);
  const isAccountPreferencesValid = () =>
    workLocation.length > 0 && communicationPreference.length > 0;

  const [designerStep, setDesignerStep] = useState(0); // Start at user type selection
  const [homeownerStep, setHomeownerStep] = useState(() => {
    // Check for explicit step parameter first
    const urlStep = searchParams.get("step");
    
    if (urlStep) {
      const stepNumber = parseInt(urlStep, 10);
      if (!isNaN(stepNumber) && stepNumber >= 0 && stepNumber <= 5) {
        return stepNumber;
      }
    }
    
    // If userType is set from URL parameters, start at step 1 (email/password)
    const urlType = searchParams.get("userType");
    const defaultStep = urlType === "Homeowner" ? 1 : 0;
    return defaultStep;
  });
  const step = userType === UserAccountType.Homeowner ? homeownerStep : designerStep;

  useEffect(() => {
    if (!user) {
      return;
    }

    if (user.onboardingStatus === "active") {
      clearRegistrationProgressStep(user);
      return;
    }

    if (user.accountType && userType !== user.accountType) {
      return;
    }

    const currentStep =
      userType === UserAccountType.Homeowner ? homeownerStep : designerStep;
    setRegistrationProgressStep(user, currentStep);
  }, [user, userType, homeownerStep, designerStep]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (user.onboardingStatus === "active") {
      clearRegistrationProgressStep(user);
      return;
    }

    if (resumeAccessGranted.current) {
      return;
    }

    const hasResumeFlagInUrl = searchParams.get("resume") === "true";
    const hasResumeIntent = hasResumeFlagInUrl || hasRegistrationResumeIntent();
    const hasInSessionProgress = !!(
      userAccountId ||
      email ||
      password ||
      confirmPassword ||
      verificationCode ||
      firstName ||
      lastName ||
      phoneNumber ||
      homeownerZip ||
      homeownerAddress.address ||
      selectedPlan ||
      resumingRegistration
    );

    if (hasResumeIntent || paymentVerified || hasInSessionProgress) {
      resumeAccessGranted.current = true;
      clearRegistrationResumeIntent();
      return;
    }

    navigate(Pages.LOGIN, { replace: true });
  }, [
    user,
    navigate,
    searchParams,
    paymentVerified,
    userAccountId,
    email,
    password,
    confirmPassword,
    verificationCode,
    firstName,
    lastName,
    phoneNumber,
    homeownerZip,
    homeownerAddress.address,
    selectedPlan,
    resumingRegistration,
  ]);
  
  const setStep = (val: number) => {
    userType === UserAccountType.Homeowner ? setHomeownerStep(val) : setDesignerStep(val);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const setNextStep = () => setStep(step + 1);
  const setPreviousStep = () => setStep(step - 1);

  // Payment step handlers
  const handlePaymentSuccess = (paymentResponse: any) => {
    
    // Get billing address from stored payment data
    const storedPaymentData = PaymentTokenManager.getPaymentData();
    
    // Pre-populate homeowner address with billing information
    if (storedPaymentData?.customerInfo) {
      setHomeownerAddress({
        firstName: storedPaymentData.customerInfo.firstName || '',
        lastName: storedPaymentData.customerInfo.lastName || '',
        address: storedPaymentData.customerInfo.address || '',
        city: storedPaymentData.customerInfo.city || '',
        state: storedPaymentData.customerInfo.state || States.Al,
        zipCode: storedPaymentData.customerInfo.zipCode || '',
        phoneNumber: storedPaymentData.customerInfo.phoneNumber || '',
      });
      if (storedPaymentData.customerInfo.zipCode) {
        setHomeownerZip(storedPaymentData.customerInfo.zipCode);
      }
    }
    
    setPaymentCompleted(true);
    setShowPaymentModal(false);
    
    // Proceed to personal details step (step 5)
    if (userType === UserAccountType.Homeowner) {
      setHomeownerStep(5);
    } else {
      setNextStep();
    }
  };

  useEffect(() => {
    if (!homeownerZip) return;
    setHomeownerAddress((prev) => ({
      ...prev,
      zipCode: prev.zipCode || homeownerZip,
    }));
  }, [homeownerZip]);

  // Check if payment is required for selected plan
  const isPaymentRequired = () => {
    return selectedPlan === 'Guild' || selectedPlan === 'GuildPlus';
  };
  

  // Payment failure handlers
  const handleSwitchToFreePlan = async () => {
    setShowPaymentFailureModal(false);
    
    try {
      // Update user to free plan and mark payment as completed
      setSelectedPlan('basic');
      setPaymentCompleted(true);
      
      // Clear all payment data since we're switching to free plan
      PaymentTokenManager.clearPaymentData();
    } catch (error) {
      // Error switching to free plan
    }
  };

  const handleRetryPayment = () => {
    setShowPaymentFailureModal(false);
    setShowPaymentModal(true);
  };

  const handlePaymentFailure = (errorMessage: string) => {
    setShowPaymentModal(false);
    setPaymentErrorMessage(errorMessage);
    setShowPaymentFailureModal(true);
  };

  // Debug payment modal state
  useEffect(() => {
    const modalOpen = showPaymentModal && !!selectedPlan && isPaymentRequired() && (!user?.id || userAccountId === 'resume-registration');
  }, [showPaymentModal, selectedPlan, user?.id, homeownerStep, userType, email, userAccountId]);

  useEffect(() => {
    // Don't enforce steps if we're currently in the loading state (creating profile)
    if (loading) {
      return;
    }
    
    // Enhanced onboarding logic - prevent bypassing steps
    if (user && user.id) {
      const userStatus = user.onboardingStatus || 'pending_email_verification';
      let requiredStep = 1;
      
      if (getEmailVerifiedFlag(user.emailVerified) === false) {
        requiredStep = 2; // Email Verification step
      } else {
        switch (userStatus) {
          case 'pending_email_verification':
            requiredStep = 2; // Email Verification step
            break;
          case 'pending_profile_completion':
            requiredStep = 5; // Address/Profile completion step (Step 5, not 4 - Step 4 was removed)
            break;
          case 'active':
            // User is fully registered - don't redirect here, let RegistrationGuard handle it
            // This prevents infinite redirect loops
            return;
          default:
            requiredStep = 1;
        }
      }
      
      // Only enforce step if user is trying to access a step beyond what they're allowed
      if (step > requiredStep) {
        setStep(requiredStep);
      }
    }

    if (userType) {
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.set("userType", userType);
      window.history.replaceState({}, "", `${window.location.pathname}?${currentParams.toString()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userType, navigate, userAccountId]);



  return (
    <div className="page registration-page">
      <div className="registration-content-wrapper">
        <form className={`registration-form ${homeownerStep === 4 && userType === UserAccountType.Homeowner ? 'registration-form-with-plans' : ''}`}>
          <>
            {step === 0 && (
              <UserTypeFieldset
                isValid={isUserTypeValid()}
                step={step}
                setNextStep={setNextStep}
                setStep={setStep}
                setUserType={setUserType}
                userType={userType}
              />
            )}

            {(() => {
              const shouldShowPlanSelection = homeownerStep === 4 && userType === UserAccountType.Homeowner;
              return shouldShowPlanSelection;
            })() && (
              <HomeownerPlanSelectionFieldset
                isValid={isPlanSelected()}
                step={step}
                setNextStep={() => {
                  
                  if (isPaymentRequired()) {
                    const paymentUserId = userAccountId || user?.id || (user as any)?._id || (user as any)?.userId;
                    const hasValidPaymentUserId = Boolean(
                      paymentUserId &&
                      paymentUserId !== 'resume-registration' &&
                      paymentUserId !== 'undefined' &&
                      paymentUserId !== 'null'
                    );

                    if (!hasValidPaymentUserId) {
                      setPaymentErrorMessage('We could not validate your account yet. Please complete email verification and try again.');
                      setShowPaymentFailureModal(true);
                      return;
                    }

                    // For paid plans, open payment modal directly
                    setShowPaymentModal(true);
                  } else {
                    // For free plans, proceed to personal details step (step 5)
                    setHomeownerStep(5);
                  }
                }}
                setStep={setStep}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
                setBillingAmount={setBillingAmount}
                setSelectedPlanData={setSelectedPlanData}
                allowedPlanKeys={isInServiceArea === false ? ["basic", "Guild"] : undefined}
                availabilityMessage={
                  isInServiceArea === false
                    ? "We're carefully expanding Home Design Guild to ensure every territory is supported by exceptional designers.\n\nWhile we're not yet live in your area, you can start your journey now through a Guild Membership - with behind-the-scenes project overviews and education at your fingertips.\n\nJoin now and be first to know when our curated selection of local interior designers becomes available."
                    : null
                }
              />
            )}
          </>
        </form>

        {/* Custom Payment Modal for Paid Plans - HOMEOWNERS ONLY */}
        {(() => {
          let homeownerPaymentId = userAccountId;
          if (
            !homeownerPaymentId ||
            homeownerPaymentId === 'resume-registration' ||
            homeownerPaymentId === 'undefined' ||
            homeownerPaymentId === 'null'
          ) {
            homeownerPaymentId = user?.id || (user as any)?._id || (user as any)?.userId;
          }

          const hasHomeownerPaymentId = Boolean(homeownerPaymentId);
          const modalOpen = showPaymentModal && userType === UserAccountType.Homeowner && homeownerStep === 4 && !!selectedPlan && isPaymentRequired() && !!email && hasHomeownerPaymentId;
          return (
            <CustomPaymentModal
            open={modalOpen}
            onClose={() => {
              setShowPaymentModal(false);
            }}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentFailure={handlePaymentFailure}
            amount={planContext.billingAmount || billingAmount}
            planName={planContext.selectedPlan?.name || selectedPlan || ''}
            planType={planContext.planType || selectedPlan || ''}
            billingCycle={planContext.billingCycle || 'Monthly'}
            email={email || ''}
            homeownerId={homeownerPaymentId || ''}
            customerInfo={{
              firstName: homeownerAddress.firstName || firstName || '',
              lastName: homeownerAddress.lastName || lastName || '',
              address: homeownerAddress.address || '',
              city: homeownerAddress.city || '',
              state: homeownerAddress.state || States.Al,
              zipCode: homeownerAddress.zipCode || '',
              phoneNumber: homeownerAddress.phoneNumber || ''
            }}
            planData={planContext.selectedPlan}
            />
          );
        })()}

        {(() => {
              const planSelected = isPlanSelected();
              const userTypeValid = isUserTypeValid();
              const paymentRequired = isPaymentRequired();
              // Don't show email/password step if user is resuming registration (they already have an account)
              const shouldShowEmailPassword = !resumingRegistration && userTypeValid && (designerStep === 1 || homeownerStep === 1);
              
              
              return shouldShowEmailPassword;
            })() && (
              <EmailPasswordFieldset
                isValid={isEmailPasswordValid()}
                step={step}
                setNextStep={() => {
                  setNextStep();
                }}
                setStep={setStep}
                setEmail={setEmail}
                setPassword={setPassword}
                setConfirmPassword={setConfirmPassword}
                email={email}
                password={password}
                confirmPassword={confirmPassword}
                setUserAccountId={setUserAccountId}
                userType={userType}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
              />
            )}

            {(() => {
              // Show verification if:
              // 1. User is resuming registration (has userAccountId and email but no password needed), OR
              // 2. New registration (email/password valid and userAccountId exists)
              const isResumingRegistration = resumingRegistration && userAccountId && email && (designerStep === 2 || homeownerStep === 2);
              const isNewRegistration = isEmailPasswordValid() && userAccountId && (designerStep === 2 || homeownerStep === 2);
              const shouldShowVerification = isResumingRegistration || isNewRegistration;
              
              return shouldShowVerification;
            })() && (
              <>
                {userType === UserAccountType.Designer ? (
                  <DesignerEmailVerificationGuard userAccountId={userAccountId}>
                    <VerificationCodeFieldset
                      isValid={isVerificationCodeValid()}
                      step={step}
                      setNextStep={() => {
                        // Mark email as verified so isVerificationCodeValid() returns true
                        setEmailVerified(true);
                        setDesignerStep(3); // Go to personal details for designers
                      }}
                      setPreviousStep={setPreviousStep}
                      setStep={setStep}
                      setVerificationCode={setVerificationCode}
                      verificationCode={verificationCode}
                      email={email}
                      userAccountId={userAccountId}
                      setUserAccountId={setUserAccountId}
                      phoneNumber={phoneNumber}
                    />
                  </DesignerEmailVerificationGuard>
                ) : (
                  <EmailVerificationGuard userAccountId={userAccountId}>
                    <VerificationCodeFieldset
                      isValid={isVerificationCodeValid()}
                      step={step}
                      setNextStep={() => {
                        // Mark email as verified so isVerificationCodeValid() returns true
                        setEmailVerified(true);
                        setHomeownerStep(3); // Go to ZIP availability step
                      }}
                      setPreviousStep={() => navigate(Pages.LOGIN)}
                      setStep={setStep}
                      setVerificationCode={setVerificationCode}
                      verificationCode={verificationCode}
                      email={email}
                      userAccountId={userAccountId}
                      setUserAccountId={setUserAccountId}
                      phoneNumber={phoneNumber}
                    />
                  </EmailVerificationGuard>
                )}
              </>
            )}

            {isVerificationCodeValid() && userType === UserAccountType.Homeowner && homeownerStep === 3 && (
              <HomeownerZipFieldset
                zipCode={homeownerZip}
                setZipCode={setHomeownerZip}
                setDmaCode={setHomeownerDmaCode}
                setIsInServiceArea={setIsInServiceArea}
                setNextStep={() => setHomeownerStep(4)}
              />
            )}

            {isVerificationCodeValid() && userType === UserAccountType.Designer && designerStep === 3 && (
              <DesignerProfileCompletionGuard step={3}>
                <PersonalDetailsFieldset
                  isValid={isPersonalDetailsValid()}
                  step={step}
                  setNextStep={() => {
                    setDesignerStep(4); // Go to business information
                  }}
                  setStep={setStep}
                  setFirstName={setFirstName}
                  setLastName={setLastName}
                  setPhoneNumber={setPhoneNumber}
                  firstName={firstName}
                  lastName={lastName}
                  phoneNumber={phoneNumber}
                  email={email}
                />
              </DesignerProfileCompletionGuard>
            )}

            {/* Payment modal opens directly from plan selection */}

            {(() => {
              const shouldRenderAddressForm = (isVerificationCodeValid() && userType === UserAccountType.Homeowner) && homeownerStep === 5;
              
              return shouldRenderAddressForm && (
              <>
                {/* Only apply ProfileCompletionGuard for non-logged-in users */}
                {user ? (
                  // Logged-in user resuming registration - bypass guard
                  <HomeownerAddressFieldset
                    step={step}
                    setStep={setStep}
                    setNextStep={() => {
                      // Mark address form as submitted and payment as completed
                      setAddressFormSubmitted(true);
                      setPaymentCompleted(true);
                    }}
                    email={email}
                    firstName={homeownerAddress.firstName}
                    lastName={homeownerAddress.lastName}
                    phoneNumber={homeownerAddress.phoneNumber}
                    zipCode={homeownerAddress.zipCode}
                    city={homeownerAddress.city}
                    state={homeownerAddress.state}
                    address={homeownerAddress.address}
                    isValid={isHomeownerAddressValid()}
                    onChange={(field, value) =>
                      setHomeownerAddress((prev) => ({ ...prev, [field]: value }))
                    }
                    userAccountId={userAccountId || undefined}
                    selectedPlan={selectedPlan || undefined}
                  />
                ) : (
                  // New registration - apply guard
                  <ProfileCompletionGuard>
                    <HomeownerAddressFieldset
                      step={step}
                      setStep={setStep}
                      setNextStep={() => {
                        // Mark address form as submitted and payment as completed
                        setAddressFormSubmitted(true);
                        setPaymentCompleted(true);
                      }}
                      email={email}
                      firstName={homeownerAddress.firstName}
                      lastName={homeownerAddress.lastName}
                      phoneNumber={homeownerAddress.phoneNumber}
                      zipCode={homeownerAddress.zipCode}
                      city={homeownerAddress.city}
                      state={homeownerAddress.state}
                      address={homeownerAddress.address}
                      isValid={isHomeownerAddressValid()}
                      onChange={(field, value) =>
                        setHomeownerAddress((prev) => ({ ...prev, [field]: value }))
                      }
                      userAccountId={userAccountId || undefined}
                      selectedPlan={selectedPlan || undefined}
                    />
                  </ProfileCompletionGuard>
                )}
              </>
              );
            })()}

            {isPersonalDetailsValid() && isVerificationCodeValid() && userType === UserAccountType.Designer && designerStep === 4 && (
              <DesignerProfileCompletionGuard step={4}>
                <BusinessInformationFieldset
                  isValid={isBusinessDetailsValid()}
                  step={step}
                  setNextStep={() => {
                    setDesignerStep(5); // Go to account preferences
                  }}
                  setStep={setStep}
                  setBusinessName={setBusinessName}
                  setBusinessAddress={setBusinessAddress}
                  setBusinessZipCode={setBusinessZipCode}
                  setBusinessCity={setBusinessCity}
                  setBusinessState={setBusinessState}
                  setBusinessWebsite={setBusinessWebsite}
                  businessName={businessName}
                  businessAddress={businessAddress}
                  businessZipCode={businessZipCode}
                  businessCity={businessCity}
                  businessState={businessState}
                  businessWebsite={businessWebsite}
                />
              </DesignerProfileCompletionGuard>
            )}

            {isBusinessDetailsValid() && isVerificationCodeValid() && userType === UserAccountType.Designer && designerStep === 5 && (
              <DesignerProfileCompletionGuard step={5}>
                <TerritorySelectionFieldset
                  isValid={isAccountPreferencesValid()}
                  step={step}
                  setNextStep={() => {
                    // Prevent multiple calls
                    if (finalizationStarted.current) {
                      return;
                    }
                    
                    // Ensure we have a user ID before proceeding
                    // IMPORTANT: If userAccountId is "resume-registration", use user?.id instead
                    // We cannot use "resume-registration" as a MongoDB ObjectId
                    let designerId = userAccountId;
                    if (designerId === 'resume-registration' || !designerId) {
                      designerId = user?.id || (user as any)?._id || (user as any)?.userId;
                      if (designerId && designerId !== 'resume-registration') {
                        // Update userAccountId with the real ID
                        setUserAccountId(designerId);
                      }
                    }
                    
                    if (!designerId || designerId === 'resume-registration') {
                      // Try to get user ID from context again
                      const effectiveUser = user;
                      if (effectiveUser) {
                        const userId = effectiveUser.id || (effectiveUser as any)._id || (effectiveUser as any).userId;
                        if (userId && userId !== 'resume-registration') {
                          setUserAccountId(userId);
                          finalizationStarted.current = true;
                          setLoading(true);
                        } else {
                          alert('Error: User account not found. Please try logging in again.');
                          navigate('/login');
                          return;
                        }
                      } else {
                        alert('Error: User account not found. Please try logging in again.');
                        navigate('/login');
                        return;
                      }
                    } else {
                      finalizationStarted.current = true;
                      setLoading(true);
                    }
                    // The UpdatingUserState component will handle the final account completion
                    // and redirect to dashboard when complete
                  }}
                  setStep={setStep}
                  setWorkLocation={setWorkLocation}
                  setCommunicationPreference={setCommunicationPreference}
                  workLocation={workLocation}
                  communicationPreference={communicationPreference}
                  setLoading={setLoading}
                />
              </DesignerProfileCompletionGuard>
            )}


    {/* Payment Failure Modal */}
    <PaymentFailureModal
      open={showPaymentFailureModal}
      onClose={() => setShowPaymentFailureModal(false)}
      onSwitchToFree={handleSwitchToFreePlan}
      onRetryPayment={handleRetryPayment}
      errorMessage={paymentErrorMessage}
    />

            {(() => {
              let homeownerCompletionId = userAccountId;
              if (
                !homeownerCompletionId ||
                homeownerCompletionId === 'resume-registration' ||
                homeownerCompletionId === 'undefined' ||
                homeownerCompletionId === 'null'
              ) {
                homeownerCompletionId = user?.id || (user as any)?._id || (user as any)?.userId;
              }

              const hasValidHomeownerId = Boolean(
                homeownerCompletionId &&
                homeownerCompletionId !== 'resume-registration' &&
                homeownerCompletionId !== 'undefined' &&
                homeownerCompletionId !== 'null'
              );

              const shouldRenderCompletion = (paymentCompleted || selectedPlan === 'basic') && 
                hasValidHomeownerId && 
                userType === UserAccountType.Homeowner && 
                isVerificationCodeValid() && 
                isHomeownerAddressValid() && 
                addressFormSubmitted;
              

              if (shouldRenderCompletion) {
                return (
                  <HomeownerRegistrationCompletion
                    homeownerId={homeownerCompletionId || ''}
                    firstName={homeownerAddress.firstName}
                    lastName={homeownerAddress.lastName}
                    phoneNumber={homeownerAddress.phoneNumber}
                    address={homeownerAddress.address}
                    city={homeownerAddress.city}
                    state={homeownerAddress.state}
                    zipCode={homeownerAddress.zipCode}
                    selectedPlan={selectedPlan || ''}
                  />
                );
              }
              return null;
            })()}

            {(() => {
              // Get designer ID from userAccountId or user context
              // IMPORTANT: If userAccountId is "resume-registration", use user?.id instead
              // We cannot use "resume-registration" as a MongoDB ObjectId
              let designerId = userAccountId;
              if (designerId === 'resume-registration' || !designerId) {
                designerId = user?.id || (user as any)?._id || (user as any)?.userId;
              }
              
              
              // Only render for DESIGNERS when we have a valid designer ID (not the placeholder)
              if (loading && userType === UserAccountType.Designer && designerId && designerId !== 'resume-registration') {
                return (
                  <UpdatingUserState
                    designerId={designerId as string}
                    firstName={firstName as string}
                    lastName={lastName as string}
                    phoneNumber={phoneNumber as string}
                    businessAddress={businessAddress as string}
                    businessName={businessName as string}
                    businessZipCode={businessZipCode as string}
                    businessCity={businessCity as string}
                    businessState={businessState as States}
                    businessWebsite={businessWebsite || ""}
                    communicationPreference={communicationPreference}
                    workLocation={workLocation}
                  />
                );
              }
              
              // If loading but no designer ID, show error
              if (loading && userType === UserAccountType.Designer && !designerId) {
                return (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>Error: User account not found. Please try logging in again.</p>
                    <button onClick={() => navigate('/login')}>Go to Login</button>
                  </div>
                );
              }
              
              return null;
            })()}

    </div>
  </div>
  );
};
